import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { apiClient } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/contexts/AuthContext';
import { 
  Clock, 
  Edit, 
  Save, 
  X, 
  Crown, 
  Trophy,
  Medal,
  Award,
  ExternalLink,
  Swords
} from 'lucide-react';
import { formatDate, extractUrls } from '@/lib/utils';
import { ChessPost } from '@/types';
import { LinkPreview } from './LinkPreview';

interface PostCardProps {
  post: ChessPost;
}

export const PostCard: React.FC<PostCardProps> = ({ post }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(post.content);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const editMutation = useMutation(
    (content: string) => apiClient.editPost(post._id, content),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('posts');
        setIsEditing(false);
        setIsSubmitting(false);
      },
      onError: () => {
        setIsSubmitting(false);
      }
    }
  );

  const handleEdit = () => {
    setIsEditing(true);
    setEditContent(post.content);
  };

  const handleSave = () => {
    if (!editContent.trim() || editContent.length > 1000) return;
    setIsSubmitting(true);
    editMutation.mutate(editContent.trim());
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditContent(post.content);
  };

  const canEdit = user?.email === post.authorEmail;

  const getRatingIcon = (rating: number) => {
    if (rating >= 2000) return <Crown className="h-4 w-4 text-yellow-500" />;
    if (rating >= 1500) return <Trophy className="h-4 w-4 text-gray-400" />;
    if (rating >= 1200) return <Medal className="h-4 w-4 text-amber-600" />;
    return <Award className="h-4 w-4 text-blue-500" />;
  };

  const getRatingColor = (rating: number) => {
    if (rating >= 2000) return 'text-yellow-600';
    if (rating >= 1500) return 'text-gray-600';
    if (rating >= 1200) return 'text-amber-600';
    return 'text-blue-600';
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-start space-x-4">
          {/* Author Avatar */}
          <div className="flex-shrink-0">
            {post.authorChessProfile?.avatar ? (
              <img
                src={post.authorChessProfile.avatar}
                alt={post.authorName}
                className="h-10 w-10 rounded-full"
              />
            ) : (
              <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                <span className="text-sm font-medium text-gray-600">
                  {post.authorName.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
          </div>

          {/* Post Content */}
          <div className="flex-1 min-w-0">
            {/* Author Info */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
              {/* Left side - Author info */}
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-gray-900">
                  {post.authorName}
                </span>
                
                {/* Chess.com Profile Badge */}
                {post.authorChessProfile?.chessUsername && (
                  <div className="flex items-center space-x-1">
                    <Crown className="h-3 w-3 text-yellow-500" />
                    <span className="text-xs text-gray-500 hidden sm:inline">Chess.com</span>
                    <a
                      href={`https://www.chess.com/member/${post.authorChessProfile.chessUsername}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:text-blue-700 flex items-center space-x-1"
                    >
                      <span>@{post.authorChessProfile.chessUsername}</span>
                      <ExternalLink className="h-2 w-2" />
                    </a>
                  </div>
                )}

                {/* Rapid Rating */}
                {post.authorChessProfile?.rapidRating && post.authorChessProfile.rapidRating > 0 && (
                  <div className="flex items-center space-x-1">
                    {getRatingIcon(post.authorChessProfile.rapidRating)}
                    <span className={`text-xs font-medium ${getRatingColor(post.authorChessProfile.rapidRating)}`}>
                      {post.authorChessProfile.rapidRating}
                    </span>
                  </div>
                )}

                <span className="text-sm text-gray-500 hidden sm:inline">•</span>
                
                {/* Timestamp */}
                <div className="flex items-center space-x-1 text-sm text-gray-500">
                  <Clock className="h-3 w-3" />
                  <span className="text-xs sm:text-sm">{formatDate(post.createdAt)}</span>
                  {post.isEdited && (
                    <span className="text-xs text-gray-400">(edited)</span>
                  )}
                </div>
              </div>

              {/* Right side - Action Buttons */}
              <div className="flex items-center space-x-2">
                {/* Challenge Button - Only show for users with Chess.com profiles */}
                {post.authorChessProfile?.chessUsername && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const challengeUrl = `https://www.chess.com/play/online/new?opponent=${post.authorChessProfile?.chessUsername}`;
                      window.open(challengeUrl, '_blank', 'noopener,noreferrer');
                    }}
                    className="flex items-center space-x-1 text-green-600 border-green-200 hover:bg-green-50 hover:border-green-300"
                  >
                    <Swords className="h-3 w-3" />
                    <span className="hidden sm:inline">Challenge</span>
                  </Button>
                )}

                {/* Edit Button */}
                {canEdit && !isEditing && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleEdit}
                    className="h-6 px-2 text-gray-400 hover:text-gray-600"
                  >
                    <Edit className="h-3 w-3" />
                  </Button>
                )}
              </div>
            </div>

            {/* Post Content */}
            {isEditing ? (
              <div className="space-y-3">
                <textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  className="w-full h-24 p-3 border border-gray-300 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  maxLength={1000}
                  placeholder="Edit your post..."
                />
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">
                    {editContent.length}/1000 characters
                  </span>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCancel}
                      disabled={isSubmitting}
                    >
                      <X className="h-4 w-4 mr-1" />
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleSave}
                      loading={isSubmitting}
                      disabled={!editContent.trim() || editContent.length > 1000 || isSubmitting}
                    >
                      <Save className="h-4 w-4 mr-1" />
                      Save
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <p className="text-gray-700 whitespace-pre-wrap">{post.content}</p>
                
                {/* Link Previews */}
                {(() => {
                  const urls = extractUrls(post.content);
                  return urls.length > 0 ? (
                    <div className="mt-3 space-y-2">
                      {urls.slice(0, 3).map((url, index) => (
                        <LinkPreview key={index} url={url} />
                      ))}
                    </div>
                  ) : null;
                })()}
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}; 